import { GlobalErrorView } from '@client/components/_core/error-handling/GlobalErrorView';
import { RouteChunkStallView } from '@client/components/_core/error-handling/RouteChunkStallView';
import { routeChunkStallCapture } from '@client/components/_core/error-handling/route-chunk-stall-capture';
import { useErrorRouteContext } from '@client/components/_core/error-handling/useErrorRouteContext';
import { getConfig } from '@client/config/runtime';
import { useAuthStore } from '@client/stores/AuthStore';
import { isCaptureLeftToServer } from '@client/trpc/error-classification';
import { isAuthRoute } from '@client/utils/common/auth';
import { RouteChunkStallError } from '@client/utils/routing/route-chunk-stall';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

type Props = Parameters<Sentry.FallbackRender>[0];

export function ErrorFallback({ error, resetError }: Props) {
    const { isPublicSection, orgId } = useErrorRouteContext();

    // Ignore legacy expired-token errors on auth routes, where a notification is already shown. Not a
    // stalled route: its code never arrived, so nothing on the page will redirect to the login, and
    // a blank page would leave no way out.
    const stalled = error instanceof RouteChunkStallError;
    const suppressFallback =
        getConfig('ENV', import.meta.env.VITE_ENV) === 'development' ||
        (!stalled && useAuthStore.getState().authTokenExpired && isAuthRoute());

    // Capture on error changes only; context rerenders must not report the same error again. A
    // refusal the server marked `clientCapture: false` is the server's to report (CUR-1962).
    useEffect(() => {
        if (error && !suppressFallback && !isCaptureLeftToServer(error)) {
            const stall = routeChunkStallCapture(error);
            Sentry.captureException(error, {
                tags: { location: 'ErrorFallback' },
                level: 'error',
                ...(stall && { fingerprint: stall.fingerprint }),
                extra: {
                    orgId,
                    isPublicSection,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    ...stall?.extra,
                },
            });
        }
    }, [error]);

    if (suppressFallback) {
        return <div />;
    }

    // A route whose code stopped arriving: the router rethrows it from below every route's boundary
    // to this one (`AppProvider`), and only a page load can fetch the code again.
    if (stalled) {
        return <RouteChunkStallView />;
    }

    return <GlobalErrorView isPublicSection={isPublicSection} orgId={orgId} error={error} reset={resetError} />;
}
